import { usePostHog } from '@posthog/react';
import { useMatch } from 'react-router-dom';

import Breadcrumbs from 'components/@extended/Breadcrumbs';
import IconButton from 'components/@extended/IconButton';
import { GlobalAddMenuItems } from 'components/buttons/GlobalAddButton';
import { useGlobalAddItems } from 'components/buttons/useGlobalAddItems';
import { ChevronDownIcon, PanelLeftOpenIcon, PlusIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { UpdateBanner } from 'components/version/UpdateBanner';
import { useIsMobile } from 'hooks/useIsMobile';
import ConcurrentEditingIndicatorBar from 'layout/MainLayout/Header/ConcurrentEditingIndicatorBar';
import ImpersonationIndicatorBar from 'layout/MainLayout/Header/ImpersonationIndicatorBar';
import MaintenanceIndicatorBar from 'layout/MainLayout/Header/MaintenanceIndicatorBar';
import { useDispatch } from 'store';
import { openDrawer } from 'store/reducers/menu';
import { t } from 'utils/getTranslations';
import BackToOrigin from './BackToOrigin';
import TimerControl from './TimerControl';

/**
 * New: what the floating + offers, in a menu at the end of the top bar. A creation opens in the create dialog.
 * A page's own action is its one filled button, so New is outlined, except on Home, which has no action of its own.
 */
function NewMenu() {
    const posthog = usePostHog();
    const items = useGlobalAddItems();
    const isHome = useMatch('/home') !== null;

    if (items.length === 0) {
        return null;
    }

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                if (open) posthog.capture('global_add_menu_opened');
            }}
        >
            <DropdownMenuTrigger
                render={
                    // Below `sm` it is the + alone, a 36px square: the label stays for assistive tech.
                    <Button
                        variant={isHome ? 'secondary' : 'outline'}
                        className="ps-3.5 pe-3 font-semibold max-sm:w-9 max-sm:px-0"
                    >
                        <PlusIcon />
                        <span className="max-sm:sr-only">{t('menu.new')}</span>
                        <ChevronDownIcon className="size-3.5 max-sm:hidden" />
                    </Button>
                }
            />
            <DropdownMenuContent align="end" className="w-auto min-w-[216px]">
                <GlobalAddMenuItems items={items} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

type Props = {
    /** Focused edit: the banners alone, as the classic header does. */
    focusedMode: boolean;
};

/**
 * The new design's top bar under today's stacked banners: on a record page the way back to where the user came
 * from, the breadcrumbs, then the timer and New in one group. Under 768px, where the breadcrumbs hide, a menu
 * button opens the sidebar drawer, and the timer and New drop their labels so the way back keeps its place.
 */
function TopBar({ focusedMode }: Props) {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    return (
        <div>
            <MaintenanceIndicatorBar />
            <UpdateBanner />
            <ImpersonationIndicatorBar />
            <ConcurrentEditingIndicatorBar />
            {!focusedMode && (
                <header className="border-neutral-tint bg-paper sticky top-0 z-(--z-nav) flex h-14 w-full items-center gap-3 border-b px-(--page-gutter-x)">
                    {isMobile && (
                        <IconButton
                            aria-label={t('navigation')}
                            onClick={() => dispatch(openDrawer({ drawerOpen: true }))}
                            variant="text"
                        >
                            <PanelLeftOpenIcon className="size-[18px]" />
                        </IconButton>
                    )}
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <BackToOrigin />
                        <div className="min-w-0 flex-1">
                            <Breadcrumbs newDesign />
                        </div>
                    </div>
                    <div data-tour="topbar-actions" className="flex min-w-0 items-center gap-2">
                        <TimerControl />
                        <NewMenu />
                    </div>
                </header>
            )}
        </div>
    );
}

export default TopBar;
